import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Clock, CheckCircle2, AlertCircle, UserRound } from "lucide-react";
import { api, errMsg, fileUrl, uploadFile } from "@/lib/api";

const EMPTY = { photo_url: "", position: "", jersey: "", home_org: "", accomplishments: "", bio: "", media_url: "" };
const FIELDS = [
  ["position", "Position", "e.g. WR / DB"],
  ["jersey", "Jersey Number", "e.g. 10"],
  ["home_org", "Home Football Organization", "e.g. St. Lucie Lions"],
  ["media_url", "Highlight Video Link (YouTube / Vimeo)", "https://"],
];

const Status = ({ req }) => {
  if (!req) return null;
  const map = {
    pending: [Clock, "text-gold", "Under review", "Tri County staff are reviewing your card. You can still update it below."],
    approved: [CheckCircle2, "text-emerald-400", "Approved & live", "Your recruiting card is on The Coast's Next Up. Submit again to request changes."],
    declined: [AlertCircle, "text-red-400", "Changes needed", "Update your details below and resubmit."],
  };
  const [Icon, color, title, body] = map[req.status] || map.pending;
  return (
    <div className="flex gap-3 border border-line bg-pitch/60 p-4" data-testid="profile-request-status">
      <Icon className={`h-6 w-6 shrink-0 ${color}`} />
      <div>
        <p className={`font-display text-xl font-extrabold uppercase italic ${color}`} data-testid={`profile-request-status-${req.status}`}>{title}</p>
        <p className="text-sm text-slate-300">{body}</p>
        {req.note && <p className="mt-2 text-sm text-slate-200" data-testid="profile-request-note">Staff note: {req.note}</p>}
      </div>
    </div>
  );
};

export const ProfileRequestCard = () => {
  const [req, setReq] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/me/profile-request").then((r) => {
      const d = r.data.request;
      setReq(d);
      if (d) setF(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] || ""])));
    }).catch(() => {});
  }, []);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file, "/me/upload");
      setF((p) => ({ ...p, photo_url: url }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.photo_url) return toast.error("Please upload a photo for your card.");
    setBusy(true);
    try {
      const r = await api.post("/me/profile-request", f);
      setReq(r.data.request);
      toast.success("Submitted! Tri County staff will review your card.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-navy p-6 sm:p-8" data-testid="profile-request-card">
      <p className="eyebrow mb-2">The Coast's Next Up</p>
      <h2 className="h-display text-4xl">My Recruiting Card</h2>
      <p className="mt-2 text-sm text-slate-400 max-w-2xl">Send your photo and football details. Staff review every submission and confirm parent/guardian permission before anything is published.</p>
      <div className="mt-6"><Status req={req} /></div>
      <form onSubmit={submit} className="mt-6 grid gap-6 lg:grid-cols-[220px_1fr]">
        <div>
          <div className="aspect-[3/4] bg-pitch border border-line overflow-hidden flex items-center justify-center">
            {f.photo_url ? <img src={fileUrl(f.photo_url)} alt="My card" className="h-full w-full object-cover" data-testid="profile-request-photo-preview" /> : <UserRound className="h-16 w-16 text-line" />}
          </div>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="profile-request-photo-file" />
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-gold text-gold py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors" data-testid="profile-request-photo-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {f.photo_url ? "Change Photo" : "Upload Photo"}
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k}><label className="label" htmlFor={`pr-${k}`}>{label}</label><input id={`pr-${k}`} className="field" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-request-${k}`} /></div>
            ))}
          </div>
          <div><label className="label" htmlFor="pr-acc">Football Accomplishments (one per line)</label><textarea id="pr-acc" rows={4} className="field" value={f.accomplishments} onChange={(e) => setF({ ...f, accomplishments: e.target.value })} data-testid="profile-request-accomplishments" /></div>
          <div><label className="label" htmlFor="pr-bio">Short Bio</label><textarea id="pr-bio" rows={3} className="field" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="profile-request-bio" /></div>
          <button type="submit" disabled={busy} className="btn-gold" data-testid="profile-request-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Submit for Review</button>
        </div>
      </form>
    </div>
  );
};
